/**
 * 注册（散户/企业分步表单）
 * ① 用户类型 → ② 手机号 → ③ 密码 → ④ 企业补充资质 → 完成跳转登录
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '@/api/auth';
import PageHero from '@/components/common/PageHero';
import '@/styles/pages/auth.css';

const STEPS = [
  { key: 0, label: '选择用户类型' },
  { key: 1, label: '手机号' },
  { key: 2, label: '设置密码' },
  { key: 3, label: '完善资料' },
];

export default function Register() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState<number>(4);
  const [username, setUsername] = useState('');
  const [realName, setRealName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [enterpriseName, setEnterpriseName] = useState('');
  const [enterpriseLicense, setEnterpriseLicense] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const next = (): boolean => {
    setError('');
    if (step === 0) {
      if (!username.trim() || !realName.trim()) {
        setError('请填写用户名与真实姓名');
        return false;
      }
      setStep(1);
      return true;
    }
    if (step === 1) {
      if (!phone.trim()) {
        setError('请填写手机号');
        return false;
      }
      setStep(2);
      return true;
    }
    if (step === 2) {
      if (password.length < 8 || password.length > 20) {
        setError('密码长度需为 8-20 位');
        return false;
      }
      if (password !== confirmPassword) {
        setError('两次输入的密码不一致');
        return false;
      }
      setStep(3);
      return true;
    }
    if (step === 3 && userType === 3) {
      if (!enterpriseName.trim() || !enterpriseLicense.trim()) {
        setError('企业注册需填写企业名称与营业执照号');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!next()) return;
    setSubmitting(true);
    setError('');
    try {
      await register({
        username,
        password,
        phone,
        real_name: realName,
        user_type: userType,
        enterprise_name: userType === 3 ? enterpriseName : undefined,
        enterprise_license: userType === 3 ? enterpriseLicense : undefined,
      });
      // 注册成功：跳转登录页，不伪造 token、不自动登录
      navigate('/login?registered=1', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : '注册失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <PageHero eyebrow="JOIN US" title="注册云牛集" desc="散户 / 企业认证注册，开启可信牛只交易" />
      <div className="container">
        <div className="auth-card auth-card--wide">
          {/* 步骤条 */}
          <div className="auth-steps">
            {STEPS.map((s) => (
              <div className={`auth-step ${step >= s.key ? 'active' : ''}`} key={s.key}>
                <span className="auth-step__no">{s.key + 1}</span>
                <span className="auth-step__label">{s.label}</span>
              </div>
            ))}
          </div>

          {step === 0 ? (
            <div className="auth-form">
              <div className="form-group">
                <label className="form-label">用户类型</label>
                <div className="auth-type-grid">
                  {[
                    [4, '散户', '个人养牛户 / 自由交易者，享受免佣优惠'],
                    [3, '企业', '养殖企业 / 农牧公司，可批量挂牌'],
                  ].map(([v, l, d]) => (
                    <label className={`auth-type-card ${userType === v ? 'active' : ''}`} key={v}>
                      <input type="radio" name="user_type" checked={userType === v} onChange={() => setUserType(v as number)} />
                      <strong>{l}</strong>
                      <span>{d}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>用户名</label>
                <input className="form-control" placeholder="设置用户名" value={username} onChange={(e) => setUsername(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>真实姓名</label>
                <input className="form-control" placeholder="请输入真实姓名" value={realName} onChange={(e) => setRealName(e.target.value)} />
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="auth-form">
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>手机号</label>
                <input className="form-control" placeholder="请输入手机号" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="form-help">短信验证暂未开放，无需填写验证码，直接填写手机号即可继续</div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="auth-form">
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>设置密码（8-20 位）</label>
                <input className="form-control" type="password" placeholder="请输入密码" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>确认密码</label>
                <input className="form-control" type="password" placeholder="请再次输入密码" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="auth-form">
              {userType === 3 ? (
                <>
                  <div className="form-group">
                    <label className="form-label"><span className="req">*</span>企业名称</label>
                    <input className="form-control" placeholder="请输入企业名称" value={enterpriseName} onChange={(e) => setEnterpriseName(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><span className="req">*</span>营业执照号</label>
                    <input className="form-control" placeholder="请输入统一社会信用代码" value={enterpriseLicense} onChange={(e) => setEnterpriseLicense(e.target.value)} />
                  </div>
                </>
              ) : (
                <div className="auth-ok">
                  🎉 散户账号信息已就绪，注册完成后即可交易（免平台服务费）
                </div>
              )}
            </div>
          ) : null}

          {error ? <div className="form-error">{error}</div> : null}

          <div className="auth-actions">
            {step > 0 ? (
              <button className="btn btn-ghost" onClick={() => setStep(step - 1)}>上一步</button>
            ) : (
              <Link className="btn btn-ghost" to="/login">已有账号？去登录</Link>
            )}
            {step < 3 ? (
              <button className="btn btn-primary" onClick={() => next()}>下一步</button>
            ) : (
              <button className="btn btn-primary btn-lg" disabled={submitting} onClick={handleSubmit}>
                {submitting ? '注册中…' : '完成注册'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
